import { PersonAddressDto, PersonDto, PersonEmailDto, PersonPhoneDto } from "@app/api2/api";
import { useFormikContext } from "formik";
import { Form as SForm, FormGroup as SFormGroup, FormField as SFormField } from "@/components/bp/bp-ui";
import SemanticFormikInput from "@common/Forms/SemanticFormikInput";
import SemanticFormikSelect from "@common/Forms/SemanticFormikSelect";
import { keyOfCheckerNested } from "@common/Utils";
import { useAddressTypeOptions, useCityOptions, useCountryOptions, usePhoneTypeOptions, useStateOptions } from "../../Hooks/LookupOptions";
import SemanticFormikCheckbox from "@common/Forms/SemanticFormikCheckbox";
import { EnrollmentFormSection, EnrollmentFormSectionWithRepeater } from "../components/EnrollmentFormSection";
import { SectionWithInfo } from "../components/SectionWithInfo";
import { Repeater } from "../components/Repeater";


const fn = keyOfCheckerNested<PersonDto>();

export function ContactTab() {

    const form = useFormikContext<PersonDto>();

    return (
        <SectionWithInfo title="Contact Information" info="Provide current or last known contact details. At least one contact method is recommended for future reference.">
            <EnrollmentFormSectionWithRepeater
                sectionKey="residential_addresses"
                header="Residential Address(es)"
                instanceType={form.values}
                name="addresses"
                newItemFactory={() => ({
                    addressLine1: '',
                    addressLine2: '',
                    addressCityName: '',
                    addressStateId: null,
                    addressPostalCode: '', addressCountryId: null, addressStateName: '',
                    addressIsPrimary: form.values.addresses.length === 0
                } as PersonAddressDto)}
                addButtonLabel="Add Address"
            >
                {({ editMode, idx, item }) => (
                    <AddressRow editMode={editMode} key={idx} address={item} idx={idx} />
                )}
            </EnrollmentFormSectionWithRepeater>
            <EnrollmentFormSectionWithRepeater
                sectionKey="phone_numbers"
                header="Phone Number(s)"
                instanceType={form.values}
                name="phones"
                newItemFactory={() => ({ phoneNumber: '', phoneTypeId: null, id: null } as PersonPhoneDto)}
                addButtonLabel="Add Phone Number"
            >
                {({ editMode, idx, item }) => (

                    <PhoneNumberRow editMode={editMode} idx={idx} row={item} />
                )}
            </EnrollmentFormSectionWithRepeater>
            <EnrollmentFormSectionWithRepeater
                sectionKey="email_addresses"
                header="Email Address(es)"
                instanceType={form.values}
                name="emails"
                newItemFactory={() => ({ emailAddress: '', id: null } as PersonEmailDto)}
                addButtonLabel="Add Email Address"
            >
                {({ editMode, idx, item }) => (
                    <SFormGroup widths="equal">
                        <SemanticFormikInput
                            disabled={!editMode}
                            name={fn(`emails.${idx}.emailAddress`)}
                            label="Email Address"
                            maxLength={320} />
                    </SFormGroup>
                )}
            </EnrollmentFormSectionWithRepeater>
        </SectionWithInfo>
    );
}

const PhoneNumberRow = ({ idx, editMode, row }) => {
    const phoneTypeOptions = usePhoneTypeOptions({ defaultValue: row.phoneTypeId });

    return <SFormGroup widths="equal">
        <SemanticFormikInput
            disabled={!editMode}
            name={fn(`phones.${idx}.phoneNumber`)}
            label="Phone Number"
            maxLength={16} />
        <SemanticFormikSelect
            disabled={!editMode}
            name={fn(`phones.${idx}.phoneTypeId`)}
            label="Type"
            placeholder="Select Type"
            {...phoneTypeOptions.getDropdownProps()} />
    </SFormGroup>;
}

const AddressRow = (props: { editMode: boolean; address: PersonAddressDto; idx: number; }) => {
    const country = useCountryOptions({ defaultValue: props.address.addressCountryId })
    const state = useStateOptions({ defaultValue: props.address.addressStateId, countryId: props.address.addressCountryId }, [props.address.addressCountryId])
    const cities = useCityOptions({ defaultValue: props.address.addressCityId, countryId: props.address.addressCountryId, stateId: props.address.addressStateId }, [props.address.addressCountryId, props.address.addressStateId])
    const addressTypeOptions = useAddressTypeOptions({ defaultValue: props.address.addressTypeId });

    // Use Formik context to update other addresses when one is set primary
    const formik = useFormikContext<PersonDto>();
    const isPrimary = !!(formik.values.addresses && formik.values.addresses[props.idx] && formik.values.addresses[props.idx].addressIsPrimary);

    const onPrimaryChange = (checked: boolean) => {
        if (checked) {
            // Set this address isPrimary = true and all others false
            (formik.values.addresses || []).forEach((_: any, i: number) => {
                formik.setFieldValue(fn(`addresses.${i}.addressIsPrimary`), i === props.idx);
                if (i === props.idx) {
                    // Clear endDate for current address
                    formik.setFieldValue(fn(`addresses.${i}.addressEndDate`), '');
                }
            });
        } else {
            // just unset this one; leave other addresses alone
            formik.setFieldValue(fn(`addresses.${props.idx}.addressIsPrimary`), false);
        }
    };

    return (<>
        <SFormGroup widths="equal">
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressTypeId`)} label="Address Type" placeholder="Select Address Type" {...addressTypeOptions.getDropdownProps()} />
            <SemanticFormikCheckbox disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressIsPrimary`)} label="Current" onChange={(e: any, data: any) => onPrimaryChange(data && data.checked)} />
        </SFormGroup>
        <SFormGroup widths="equal">
            <SemanticFormikInput disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressLine1`)} label="Address Line 1" maxLength={100} required />
            <SemanticFormikInput disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressLine2`)} label="Address Line 2" maxLength={100} />
        </SFormGroup>
        <SFormGroup widths={'4'}>
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressCountryId`)} required label="Country" {...country.getDropdownProps()} placeholder="Select Country" />
            <SFormField />
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressStateId`)} clearable label="State" {...state.getDropdownProps()} placeholder="Select State" required={!props.address.addressStateName} />
            {/* <SemanticFormikInput name={fn(`addresses.${props.idx}.stateName`)} label="State Name (textual)" maxLength={100} required={!props.address.stateId} /> */}
        </SFormGroup>
        <SFormGroup widths={'4'}>
            <SemanticFormikInput disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressPostalCode`)} label="Postal Code" maxLength={20} required />
            <SFormField />
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`addresses.${props.idx}.addressCityId`)} limit={0} placeholder="Start typing..." clearable label="City/Town" {...cities.getDropdownProps()} required={!props.address.addressCityName} />
            {/* <SemanticFormikInput name={fn(`addresses.${props.idx}.cityName`)} label="City/Town (textual)" maxLength={100} required={!props.address.cityId} /> */}

        </SFormGroup>
        <SFormGroup widths="equal">
            <SemanticFormikInput disabled={!props.editMode} type="date" name={fn(`addresses.${props.idx}.addressStartDate`)} label="Start Date" maxLength={20} />
            {isPrimary ? (
                <SFormField>
                    <label>End Date</label>
                    <div style={{ paddingTop: '0.785714rem' }}>Current</div>
                </SFormField>
            ) : (
                <SemanticFormikInput disabled={!props.editMode} type="date" name={fn(`addresses.${props.idx}.addressEndDate`)} label="End Date" maxLength={20} />
            )}
        </SFormGroup>
    </>);
}

